import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { teacherPortalAPI } from '../utils/api';
import type { TeacherAuthContextType, TeacherProfile } from '../types/teacher';

const TeacherAuthContext = createContext<TeacherAuthContextType | undefined>(undefined);

const TEACHER_STORAGE_KEY = 'teacher_user';
const TEACHER_TOKEN_KEY = 'teacher_jwttoken';
const TEACHER_REFRESH_KEY = 'teacher_refresh_token';

export const useTeacherAuth = () => {
  const context = useContext(TeacherAuthContext);
  if (!context) {
    throw new Error('useTeacherAuth must be used within a TeacherAuthProvider');
  }
  return context;
};

export const TeacherAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [teacher, setTeacher] = useState<TeacherProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedTeacher = localStorage.getItem(TEACHER_STORAGE_KEY);
    const token = localStorage.getItem(TEACHER_TOKEN_KEY);
    if (storedTeacher && token) {
      try {
        setTeacher(JSON.parse(storedTeacher));
      } catch {
        localStorage.removeItem(TEACHER_STORAGE_KEY);
        localStorage.removeItem(TEACHER_TOKEN_KEY);
        localStorage.removeItem(TEACHER_REFRESH_KEY);
      }
    }
    setLoading(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    const response = await teacherPortalAPI.getMe();
    const profile = response.data || response.teacher || response.profile || response.user;
    if (profile) {
      setTeacher(profile);
      localStorage.setItem(TEACHER_STORAGE_KEY, JSON.stringify(profile));
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await teacherPortalAPI.login({ username, password });
      if (response.success === false || response.success === 0) {
        throw new Error(response.message || 'Invalid username or password.');
      }
      const profile = response.profile || response.teacher || response.user;
      const token = response.access_token || response.jwttoken || profile?.jwttoken;
      if (!profile || !token) {
        throw new Error(response.message || 'Login response did not include a teacher profile or token.');
      }
      localStorage.setItem(TEACHER_STORAGE_KEY, JSON.stringify(profile));
      localStorage.setItem(TEACHER_TOKEN_KEY, token);
      if (response.refresh_token) {
        localStorage.setItem(TEACHER_REFRESH_KEY, response.refresh_token);
      }
      setTeacher(profile);
      return profile;
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to sign in.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TEACHER_STORAGE_KEY);
    localStorage.removeItem(TEACHER_TOKEN_KEY);
    localStorage.removeItem(TEACHER_REFRESH_KEY);
    setTeacher(null);
    setError(null);
  }, []);

  const markPasswordChanged = useCallback(() => {
    setTeacher((current) => {
      if (!current) return current;
      const updated = { ...current, must_change_password: false };
      localStorage.setItem(TEACHER_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value: TeacherAuthContextType = {
    teacher,
    loading,
    error,
    login,
    logout,
    clearError: () => setError(null),
    refreshProfile,
    markPasswordChanged,
  };

  return <TeacherAuthContext.Provider value={value}>{children}</TeacherAuthContext.Provider>;
};
